import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ageGroupsAPI } from '../../services/api';
import { AgeGroupPlayer, AgeGroupSelect } from '../../types/ageGroup';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

interface PlayerAgeGroupConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: AgeGroupPlayer | null;
  targetAgeGroupId: number;
  targetAgeGroupName: string;
  onConfirm: (removeFromAgeGroupIds: number[]) => void;
}

const PlayerAgeGroupConfirmModal: React.FC<PlayerAgeGroupConfirmModalProps> = ({
  isOpen,
  onClose,
  player,
  targetAgeGroupId,
  targetAgeGroupName,
  onConfirm,
}) => {
  const [currentAgeGroups, setCurrentAgeGroups] = useState<AgeGroupSelect[]>([]);
  const [selectedToRemove, setSelectedToRemove] = useState<number[]>([]);
  const [showRemoveOptions, setShowRemoveOptions] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadPlayerAgeGroups = useCallback(async () => {
    if (!player) return;

    setLoading(true);
    try {
      const result = await ageGroupsAPI.getPlayerAgeGroups(player.id);
      // Filter out the target age group from the list
      const otherAgeGroups = result.ageGroups.filter(ag => ag.id !== targetAgeGroupId);
      setCurrentAgeGroups(otherAgeGroups);
      setSelectedToRemove([]);
      setShowRemoveOptions(false);
    } catch (error) {
      console.error('Failed to load player age groups:', error);
    } finally {
      setLoading(false);
    }
  }, [player, targetAgeGroupId]);

  useEffect(() => {
    if (isOpen && player) {
      loadPlayerAgeGroups();
    }
  }, [isOpen, player, loadPlayerAgeGroups]);

  const handleNoRemove = () => {
    // Keep player in all age groups (many-to-many)
    onConfirm([]);
  };

  const handleYesRemove = () => {
    if (currentAgeGroups.length === 1) {
      // Only one other age group - remove from that one
      onConfirm(currentAgeGroups.map(ag => ag.id));
    } else {
      // Multiple age groups - show selection options
      setShowRemoveOptions(true);
    }
  };

  const handleToggleAgeGroup = (ageGroupId: number) => {
    setSelectedToRemove(prev =>
      prev.includes(ageGroupId)
        ? prev.filter(id => id !== ageGroupId)
        : [...prev, ageGroupId]
    );
  };

  const handleSelectAll = () => {
    setSelectedToRemove(currentAgeGroups.map(ag => ag.id));
  };

  const handleConfirmRemove = () => {
    onConfirm(selectedToRemove);
  };

  useEscapeToClose(isOpen && !!player, onClose);

  if (!isOpen || !player) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label={'Korosztályok megerősítése'}>
      <div className="flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="modal-panel-mobile relative flex w-full flex-col transform bg-white text-left shadow-xl transition-all md:my-8 md:max-w-md md:rounded-lg md:overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 md:px-6 md:py-4">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">
              Játékos hozzáadása: {player.name}
            </h3>
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pt-4 pb-safe-4 md:px-6">
            <div className="w-full text-left">
              {loading ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">Betöltés...</p>
                </div>
              ) : currentAgeGroups.length === 0 ? (
                // Player is not in any other age group
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    A játékos hozzáadása a(z) <strong>{targetAgeGroupName}</strong> korosztályhoz.
                  </p>
                  <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end md:gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full md:w-auto rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Mégse
                    </button>
                    <button
                      type="button"
                      onClick={() => onConfirm([])}
                      className="w-full md:w-auto rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                    >
                      Hozzáadás
                    </button>
                  </div>
                </div>
              ) : !showRemoveOptions ? (
                // Ask if they want to remove from other age groups
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    <strong>{player.name}</strong> jelenleg a következő korosztály(ok)ban van:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-700 bg-gray-50 rounded-md p-3">
                    {currentAgeGroups.map(ag => (
                      <li key={ag.id}>{ag.name}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600">
                    Szeretnéd törölni a korábbi korosztály(ok)ból?
                  </p>
                  <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end md:gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full md:w-auto rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Mégse
                    </button>
                    <button
                      type="button"
                      onClick={handleNoRemove}
                      className="w-full md:w-auto rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                    >
                      Nem, maradjon mindben
                    </button>
                    <button
                      type="button"
                      onClick={handleYesRemove}
                      className="w-full md:w-auto rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                    >
                      Igen, törlés
                    </button>
                  </div>
                </div>
              ) : (
                // Show checkboxes to select which age groups to remove from
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Melyik korosztály(ok)ból szeretnéd törölni?
                  </p>
                  <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-200">
                    {currentAgeGroups.map(ag => (
                      <label
                        key={ag.id}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedToRemove.includes(ag.id)}
                          onChange={() => handleToggleAgeGroup(ag.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm text-gray-900">{ag.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="inline-flex min-h-9 items-center text-sm text-primary-600 hover:text-primary-500"
                    >
                      Összes kijelölése
                    </button>
                    <div className="flex flex-col-reverse gap-2 md:flex-row md:gap-3">
                      <button
                        type="button"
                        onClick={() => setShowRemoveOptions(false)}
                        className="w-full md:w-auto rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        Vissza
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmRemove}
                        className="w-full md:w-auto rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                      >
                        Megerősítés
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerAgeGroupConfirmModal;
